import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'

import { ApproveAction } from '../flavours/approve/types'
import { ResetApprovalAction } from '../flavours/reset-approval/types'
import { Action, ActionContext } from './types'
import { Token } from '@/domain/types/Token'

export interface GetApproveActionsParams {
  token: Token
  value: NormalizedNumber
  requiredValue?: NormalizedNumber
  spender: CheckedAddress
  context: ActionContext
}

export function getApproveActions({
  token,
  value,
  requiredValue,
  spender,
  context,
}: GetApproveActionsParams): Action[] {
  const approveAction = ApproveAction({
    type: 'approve',
    token,
    spender,
    value,
    requiredValue,
  })

  const foundApproval = context.tokenApprovals?.find(
    (approval) => approval.approval > 0 && approval.token === token.address && approval.spender === spender,
  )
  const hasApprovalReceipt = context.txReceipts.some(
    ([action]) => action.type === 'approve' && action.token.address === token.address && action.spender === spender,
  )
  const hasResetApprovalReceipt = context.txReceipts.some(
    ([action]) =>
      action.type === 'resetApproval' && action.token.address === token.address && action.spender === spender,
  )

  const approvalActionWillBeSkipped =
    foundApproval?.approval && foundApproval.approval >= token.toBigInt(requiredValue ?? value)

  // Reset is needed when user has too small approval set with usdt token.
  // foundApproval check is used to show reset approval action only when user has some approval already set when entering the dialog.
  // Receipts checking are used to make sure that reset approval action will not disappear after clicking reset approval.
  if (
    import.meta.env.VITE_FEATURE_RESET_APPROVAL === '1' &&
    ((foundApproval && !hasApprovalReceipt && !approvalActionWillBeSkipped) || hasResetApprovalReceipt)
  ) {
    const resetApprovalAction = ResetApprovalAction({
      type: 'resetApproval',
      token,
      spender,
      value,
      requiredValue,
    })
    return [resetApprovalAction, approveAction]
  }

  return [approveAction]
}
